import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button } from '../shared/Button';
import { useToast } from '../shared/Toast';

/**
 * QuickActions Component
 * Provides predefined quick action buttons for common AI caption regeneration requests
 */
export function QuickActions({ imageId, onRegenerate }) {
  const { t } = useTranslation('approval');
  const toast = useToast();
  const [loadingAction, setLoadingAction] = useState(null);

  // Define quick actions with i18n labels and feedback
  const quickActions = [
    {
      id: 'shorter',
      label: t('quickActions.actions.shorter.label'),
      feedback: t('quickActions.actions.shorter.feedback'),
      temperature: 0.7
    },
    {
      id: 'longer',
      label: t('quickActions.actions.longer.label'),
      feedback: t('quickActions.actions.longer.feedback'),
      temperature: 0.8
    },
    {
      id: 'funnier',
      label: t('quickActions.actions.funnier.label'),
      feedback: t('quickActions.actions.funnier.feedback'),
      temperature: 0.9
    },
    {
      id: 'formal',
      label: t('quickActions.actions.formal.label'),
      feedback: t('quickActions.actions.formal.feedback'),
      temperature: 0.6
    },
    {
      id: 'casual',
      label: t('quickActions.actions.casual.label'),
      feedback: t('quickActions.actions.casual.feedback'),
      temperature: 0.8
    },
    {
      id: 'emojis',
      label: t('quickActions.actions.moreEmojis.label'),
      feedback: t('quickActions.actions.moreEmojis.feedback'),
      temperature: 0.7
    },
    {
      id: 'hashtags',
      label: t('quickActions.actions.fewerHashtags.label'),
      feedback: t('quickActions.actions.fewerHashtags.feedback'),
      temperature: 0.7
    }
  ];

  /**
   * Handle quick action click
   * @param {Object} action - The action object containing feedback and temperature
   */
  const handleActionClick = async (action) => {
    setLoadingAction(action.id);

    try {
      // Call the onRegenerate callback with feedback and temperature
      await onRegenerate({
        feedback: action.feedback,
        temperature: action.temperature
      });

      toast.success(t('quickActions.toast.regenerating'));
    } catch (error) {
      console.error('Error regenerating caption:', error);
      toast.error(t('quickActions.toast.error'));
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700">
        {t('quickActions.title')}
      </h4>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {quickActions.map((action) => (
          <Button
            key={action.id}
            variant="secondary"
            size="sm"
            onClick={() => handleActionClick(action)}
            loading={loadingAction === action.id}
            disabled={loadingAction !== null && loadingAction !== action.id}
            className="w-full"
            aria-label={`${action.label} - ${action.feedback}`}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

// PropTypes validation
QuickActions.propTypes = {
  imageId: PropTypes.string.isRequired,
  onRegenerate: PropTypes.func.isRequired
};

export default QuickActions;